import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { DatePickerID } from "./DatePickerID";
import { CircleNotch } from "@phosphor-icons/react";

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export default function EditClaimDialog({ claim, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [regions, setRegions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/regions").then((r) => setRegions(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!claim) { setForm(null); return; }
    setForm({
      policy_number: claim.policy_number || "", policy_start_date: (claim.policy_start_date || "").slice(0, 10),
      incident_date: (claim.incident_date || "").slice(0, 10), claim_amount: claim.claim_amount ?? "",
      province: claim.province || "", city: claim.city || "", reporter_name: claim.reporter_name || "",
      previous_claims_count: claim.previous_claims_count ?? 0, documents_complete: !!claim.documents_complete,
      chronology: claim.chronology || "",
      insured_name: claim.insured_name || "", insured_age: claim.insured_age ?? "",
      cause_of_death: claim.cause_of_death || "", beneficiary_relation: claim.beneficiary_relation || "",
      vehicle_brand: claim.vehicle_brand || "", plate_number: claim.plate_number || "",
      vehicle_year: claim.vehicle_year ?? "", vehicle_value: claim.vehicle_value ?? "",
      incident_type: claim.incident_type || "", police_report: !!claim.police_report,
      incident_hour: claim.incident_hour ?? "", witness_present: !!claim.witness_present,
    });
  }, [claim]);

  if (!claim || !form) return null;
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const isJiwa = claim.claim_type === "jiwa";

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        claim_amount: Number(form.claim_amount),
        previous_claims_count: Number(form.previous_claims_count) || 0,
        insured_age: form.insured_age !== "" ? Number(form.insured_age) : null,
        vehicle_year: form.vehicle_year !== "" ? Number(form.vehicle_year) : null,
        vehicle_value: form.vehicle_value !== "" ? Number(form.vehicle_value) : null,
        incident_hour: form.incident_hour !== "" ? Number(form.incident_hour) : null,
      };
      const { data } = await api.put(`/claims/${claim.id}`, payload);
      toast.success(`Klaim ${data.claim_number} diperbarui — skor baru ${data.score} (${data.risk_category})`);
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!claim} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-claim-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base">Edit Klaim {claim.claim_number}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">Skor akan dihitung ulang otomatis setelah disimpan.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <F label="Nomor Polis"><Input data-testid="edit-policy-number" value={form.policy_number} onChange={(e) => set("policy_number", e.target.value)} /></F>
          <F label="Mulai Polis"><DatePickerID testId="edit-policy-start-date" value={form.policy_start_date} onChange={(v) => set("policy_start_date", v)} /></F>
          <F label="Tgl Kejadian"><DatePickerID testId="edit-incident-date" value={form.incident_date} onChange={(v) => set("incident_date", v)} disabled={{ after: new Date() }} /></F>
          <F label="Nilai Klaim (Rp)"><Input data-testid="edit-claim-amount" type="number" value={form.claim_amount} onChange={(e) => set("claim_amount", e.target.value)} /></F>
          <F label="Provinsi">
            <Select value={form.province} onValueChange={(v) => set("province", v)}>
              <SelectTrigger data-testid="edit-province"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">{regions.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Kota"><Input data-testid="edit-city" value={form.city} onChange={(e) => set("city", e.target.value)} /></F>
          <F label="Nama Pelapor"><Input data-testid="edit-reporter-name" value={form.reporter_name} onChange={(e) => set("reporter_name", e.target.value)} /></F>
          <F label="Klaim Sebelumnya"><Input data-testid="edit-previous-claims" type="number" min="0" value={form.previous_claims_count} onChange={(e) => set("previous_claims_count", e.target.value)} /></F>
          <div className="flex items-center gap-2 pt-6">
            <Switch data-testid="edit-documents-complete" checked={form.documents_complete} onCheckedChange={(v) => set("documents_complete", v)} />
            <Label className="text-sm">Dokumen lengkap</Label>
          </div>
        </div>
        <F label="Kronologi"><Textarea data-testid="edit-chronology" rows={4} value={form.chronology} onChange={(e) => set("chronology", e.target.value)} /></F>

        {isJiwa ? (
          <div className="grid grid-cols-2 gap-3 border-t border-border pt-3">
            <F label="Nama Tertanggung"><Input data-testid="edit-insured-name" value={form.insured_name} onChange={(e) => set("insured_name", e.target.value)} /></F>
            <F label="Usia"><Input data-testid="edit-insured-age" type="number" value={form.insured_age} onChange={(e) => set("insured_age", e.target.value)} /></F>
            <F label="Penyebab Kematian">
              <Select value={form.cause_of_death} onValueChange={(v) => set("cause_of_death", v)}>
                <SelectTrigger data-testid="edit-cause-of-death"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sakit">Sakit</SelectItem>
                  <SelectItem value="kecelakaan">Kecelakaan</SelectItem>
                  <SelectItem value="tidak_diketahui">Tidak Diketahui</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Hubungan Ahli Waris"><Input value={form.beneficiary_relation} onChange={(e) => set("beneficiary_relation", e.target.value)} /></F>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 border-t border-border pt-3">
            <F label="Nama Tertanggung"><Input data-testid="edit-insured-name" value={form.insured_name} onChange={(e) => set("insured_name", e.target.value)} /></F>
            <F label="Merek Kendaraan"><Input value={form.vehicle_brand} onChange={(e) => set("vehicle_brand", e.target.value)} /></F>
            <F label="No. Polisi"><Input data-testid="edit-plate-number" value={form.plate_number} onChange={(e) => set("plate_number", e.target.value)} /></F>
            <F label="Tahun"><Input type="number" value={form.vehicle_year} onChange={(e) => set("vehicle_year", e.target.value)} /></F>
            <F label="Nilai Kendaraan (Rp)"><Input data-testid="edit-vehicle-value" type="number" value={form.vehicle_value} onChange={(e) => set("vehicle_value", e.target.value)} /></F>
            <F label="Jenis Kejadian">
              <Select value={form.incident_type} onValueChange={(v) => set("incident_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="kecelakaan">Kecelakaan</SelectItem>
                  <SelectItem value="pencurian">Pencurian</SelectItem>
                  <SelectItem value="kebakaran">Kebakaran</SelectItem>
                  <SelectItem value="kehilangan_total">Kehilangan Total</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Jam Kejadian (0-23)"><Input type="number" min="0" max="23" value={form.incident_hour} onChange={(e) => set("incident_hour", e.target.value)} /></F>
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={form.police_report} onCheckedChange={(v) => set("police_report", v)} />
              <Label className="text-sm">Laporan polisi</Label>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={form.witness_present} onCheckedChange={(v) => set("witness_present", v)} />
              <Label className="text-sm">Ada saksi</Label>
            </div>
          </div>
        )}

        <Button data-testid="edit-claim-save" onClick={save} disabled={saving || !form.reporter_name || !form.chronology || !form.claim_amount}
          className="w-full bg-slate-900 hover:bg-slate-800 rounded-none">
          {saving ? <CircleNotch size={16} className="animate-spin" /> : "Simpan & Hitung Ulang Skor"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
